import React from 'react';
import {DatePicker, Select} from 'antd';
import {DownOutlined} from '@ant-design/icons';
import type {Dayjs} from 'dayjs';
import {useTranslation} from 'react-i18next';
import BatchInputFilterItem from '../../../delivery/components/BatchInputFilterItem';
import {usePodReviewOptions} from '../../podReview/hooks/usePodReviewOptions';
import {getSmartLabelClassName} from '../../../../utils/labelWidthUtils';
import {
    useFilterContainerClassName,
    useFilterInputContainerClassName,
    useFilterLabelClassName
} from '../../../../utils/languageUtils';
import {PodRiskReviewFilters, PodRiskReviewStatus} from '../graphql/types/PodRiskReviewType';
import type {PodRiskTypeOptionItem} from '../hooks/usePodRiskTypeOptions';

const {RangePicker} = DatePicker;

/** 司机远程搜索触发的最小关键字长度；与 POD 审核页保持一致 */
const DRIVER_SEARCH_MIN_LENGTH = 2;

/** 未选中时的共享空数组，保证 useMemo 依赖引用稳定 */
const EMPTY_SELECTION: string[] = [];

/**
 * 时间区间上限 1 个月，双向限制（选 start 后限 end；选 end 后限 start）。
 * info.from 由 antd RangePicker 提供。
 */
const buildOneMonthDisabledDate = () => (current: Dayjs, info?: {from?: Dayjs}) => {
    if (!current || !info?.from) return false;
    return Math.abs(current.diff(info.from, 'day')) > 31;
};

interface PodRiskReviewFilterProps {
    filters: PodRiskReviewFilters;
    onChange: (filters: PodRiskReviewFilters) => void;
    /** 风险类型下拉选项（服务端字典，已按 displayOrder 排序） */
    riskTypeOptions: PodRiskTypeOptionItem[];
    /** 配送商下拉 */
    dspOptions: {value: string; label: string}[];
}

interface SingleSelectItemProps {
    label: string;
    value?: string;
    onChange: (value: string | undefined) => void;
    options: {label: string; value: string}[];
    spanClassName: string;
    placeholder: string;
}

interface MultiSelectItemProps {
    label: string;
    value?: string[];
    onChange: (value: string[] | undefined) => void;
    options: {label: string; value: string}[];
    spanClassName: string;
    placeholder: string;
}

/** 多选下拉筛选项；components/filters/ 下的既有组件均绑定了各自数据源，故就地实现 */
const MultiSelectFilterItem: React.FC<MultiSelectItemProps> = ({
    label,
    value,
    onChange,
    options,
    spanClassName,
    placeholder
}) => {
    const containerClassName = useFilterContainerClassName();
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    size="middle"
                    className="w-full"
                    maxTagCount="responsive"
                    maxTagTextLength={10}
                    placeholder={placeholder}
                    value={value}
                    onChange={val => onChange(val && val.length > 0 ? val : undefined)}
                    options={options}
                    filterOption={(input, option) =>
                        String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                />
            </div>
        </div>
    );
};

/** 单选下拉筛选项 */
const SingleSelectFilterItem: React.FC<SingleSelectItemProps> = ({
    label,
    value,
    onChange,
    options,
    spanClassName,
    placeholder
}) => {
    const containerClassName = useFilterContainerClassName();
    const labelClassName = useFilterLabelClassName(spanClassName);
    const inputContainerClassName = useFilterInputContainerClassName();

    return (
        <div className={containerClassName}>
            <span className={labelClassName}>{label}</span>
            <div className={inputContainerClassName}>
                <Select
                    showSearch
                    allowClear
                    size="middle"
                    className="w-full"
                    placeholder={placeholder}
                    value={value || undefined}
                    onChange={val => onChange(val || undefined)}
                    options={options}
                    filterOption={(input, option) =>
                        String(option?.label ?? '')
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                />
            </div>
        </div>
    );
};

const PodRiskReviewFilter: React.FC<PodRiskReviewFilterProps> = ({
    filters,
    onChange,
    riskTypeOptions,
    dspOptions
}) => {
    const {t} = useTranslation();

    const update = (patch: Partial<PodRiskReviewFilters>) => onChange({...filters, ...patch});

    // 未选 DSP 时司机候选集是全美司机，靠关键字远程搜索收窄
    const [driverKeyword, setDriverKeyword] = React.useState('');
    // 空值回落用固定引用，避免每次 render 生成新数组导致下方 useMemo 依赖恒变
    const selectedDsps = filters.dspIds ?? EMPTY_SELECTION;
    const selectedHubs = filters.hubIds ?? EMPTY_SELECTION;
    const selectedWarehouses = filters.warehouseIds ?? EMPTY_SELECTION;
    const selectedAreas = filters.areaSortingCodes ?? EMPTY_SELECTION;
    const isDriverRemoteMode = selectedDsps.length === 0;

    // DSP 由页面下发（列表名称映射同源），此处只取仓库/区域/司机/审核人
    const {hubs, warehouses, areas, drivers, reviewers, loading} = usePodReviewOptions(
        selectedDsps,
        driverKeyword
    );

    const filteredWarehouses = React.useMemo(() => {
        if (selectedHubs.length === 0) return warehouses;
        const hubSet = new Set(selectedHubs);
        return warehouses.filter(whs => !whs.hubId || hubSet.has(whs.hubId));
    }, [warehouses, selectedHubs]);

    const filteredAreas = React.useMemo(() => {
        if (selectedWarehouses.length > 0) {
            const warehouseSet = new Set(selectedWarehouses);
            return areas.filter(area => area.warehouseId && warehouseSet.has(area.warehouseId));
        }
        if (selectedHubs.length > 0) {
            const allowedWarehouseIds = new Set(filteredWarehouses.map(whs => whs.id));
            return areas.filter(area => area.warehouseId && allowedWarehouseIds.has(area.warehouseId));
        }
        return areas;
    }, [areas, filteredWarehouses, selectedWarehouses, selectedHubs]);

    const filteredDrivers = React.useMemo(() => {
        if (selectedDsps.length === 0) return drivers;
        const dspSet = new Set(selectedDsps);
        return drivers.filter(driver => !driver.dspId || dspSet.has(driver.dspId));
    }, [drivers, selectedDsps]);

    /**
     * 中心变更：同时收敛已选仓库与区域到新中心范围内，避免残留互斥条件查不出数据。
     * 后端按三段码筛选，故一并把选中中心的 sortingCode 带上。
     */
    const handleHubChange = (value: string[] | undefined) => {
        const hubIds = value ?? [];
        const hubSortingCodes = hubIds
            .map(id => hubs.find(hub => hub.id === id)?.sortingCode)
            .filter((code): code is string => !!code);

        const allowedWarehouseIds = new Set(
            warehouses
                .filter(whs => hubIds.length === 0 || !whs.hubId || hubIds.includes(whs.hubId))
                .map(whs => whs.id)
        );
        const nextWarehouseIds = selectedWarehouses.filter(id => allowedWarehouseIds.has(id));

        const allowedAreaCodes = new Set(
            areas
                .filter(area => !area.warehouseId || allowedWarehouseIds.has(area.warehouseId))
                .map(area => area.sortingCode)
                .filter((code): code is string => !!code)
        );
        const nextAreaCodes = selectedAreas.filter(code => allowedAreaCodes.has(code));

        update({
            hubIds: hubIds.length > 0 ? hubIds : undefined,
            hubSortingCodes: hubSortingCodes.length > 0 ? hubSortingCodes : undefined,
            warehouseIds: nextWarehouseIds.length > 0 ? nextWarehouseIds : undefined,
            areaSortingCodes: nextAreaCodes.length > 0 ? nextAreaCodes : undefined
        });
    };

    /** 仓库变更：收敛已选区域到新仓库范围内 */
    const handleWarehouseChange = (value: string[] | undefined) => {
        const warehouseIds = value ?? [];
        const warehouseSet = warehouseIds.length > 0
            ? new Set(warehouseIds)
            : (selectedHubs.length > 0
                ? new Set(filteredWarehouses.map(whs => whs.id))
                : null);

        const allowedAreaCodes = new Set(
            areas
                .filter(area => !area.warehouseId || !warehouseSet || warehouseSet.has(area.warehouseId))
                .map(area => area.sortingCode)
                .filter((code): code is string => !!code)
        );
        const nextAreaCodes = selectedAreas.filter(code => allowedAreaCodes.has(code));

        update({
            warehouseIds: warehouseIds.length > 0 ? warehouseIds : undefined,
            areaSortingCodes: nextAreaCodes.length > 0 ? nextAreaCodes : undefined
        });
    };

    /** 配送商变更：清空已选司机，避免残留不属于新配送商的司机 */
    const handleDspChange = (value: string[] | undefined) => {
        update({dspIds: value, driverIds: undefined});
    };

    const hubOptions = hubs.map(hub => ({
        value: hub.id,
        label: `${hub.name}${hub.sortingCode ? ` (${hub.sortingCode})` : ''}`
    }));

    const warehouseOptions = filteredWarehouses.map(whs => ({
        value: whs.id,
        label: `${whs.name}${whs.sortingCode ? ` (${whs.sortingCode})` : ''}`
    }));

    const areaOptions = filteredAreas
        .filter(area => !!area.sortingCode)
        .map(area => ({
            value: area.sortingCode as string,
            label: `${area.name} (${area.sortingCode})`
        }));

    const driverOptions = filteredDrivers.map(driver => ({
        value: driver.id,
        label: `${driver.name}${driver.code ? ` (${driver.code})` : ''}`
    }));

    const reviewerOptions = reviewers.map(reviewer => ({
        value: reviewer.id,
        label: reviewer.name
    }));

    const labelTrackingNumbers = t('podRiskReview.filter.trackingNumbers');
    const labelChannelOrderNumbers = t('podRiskReview.filter.channelOrderNumbers');
    const labelHub = t('podRiskReview.filter.hub');
    const labelWarehouse = t('podRiskReview.filter.warehouse');
    const labelArea = t('podRiskReview.filter.area');
    const labelDsp = t('podRiskReview.filter.dsp');
    const labelDriver = t('podRiskReview.filter.driver');
    const labelReviewer = t('podRiskReview.filter.reviewer');
    const labelStatus = t('podRiskReview.filter.status');
    const labelRiskTypes = t('podRiskReview.filter.riskTypes');
    const labelDeliveredAt = t('podRiskReview.filter.deliveredAt');
    const labelCreatedAt = t('podRiskReview.filter.createdAt');
    const labelReviewedAt = t('podRiskReview.filter.reviewedAt');

    // 同列 label 宽度统一：各列取本列全部 label 计算（须与下方 grid 的实际列位置一致）
    // 第 1 列：运单号 / 订单号（独占整行，label 仍属本列）/ 中心 / 配送商 / 审核状态 / 妥投时间
    const col1SpanClassName = getSmartLabelClassName([
        labelTrackingNumbers,
        labelChannelOrderNumbers,
        labelHub,
        labelDsp,
        labelStatus,
        labelDeliveredAt
    ]);
    // 第 2 列：仓库 / 司机 / 风险类型 / 创建时间
    const col2SpanClassName = getSmartLabelClassName([
        labelWarehouse,
        labelDriver,
        labelRiskTypes,
        labelCreatedAt
    ]);
    // 第 3 列：区域 / 审核人 / 审核时间（与审核状态、风险类型同行）
    const col3SpanClassName = getSmartLabelClassName([
        labelArea,
        labelReviewer,
        labelReviewedAt
    ]);

    // 时间项用裸 RangePicker，需自行套用 FilterItem 的样式结构
    const containerClassName = useFilterContainerClassName();
    const inputContainerClassName = useFilterInputContainerClassName();
    const labelCls1 = useFilterLabelClassName(col1SpanClassName);
    const labelCls2 = useFilterLabelClassName(col2SpanClassName);
    const labelCls3 = useFilterLabelClassName(col3SpanClassName);

    const statusOptions = [
        PodRiskReviewStatus.PENDING_REVIEW,
        PodRiskReviewStatus.APPROVED,
        PodRiskReviewStatus.REJECTED,
        PodRiskReviewStatus.CANCELLED
    ].map(status => ({
        label: t(`podRiskReview.enum.status.${status}`) as string,
        value: status as string
    }));

    const allPlaceholder = t('podRiskReview.filter.all') as string;

    return (
        <div className="mb-2 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                {/* 运单号与订单号为主查询项，各占整行 */}
                <div className="min-w-0 col-span-3">
                    <BatchInputFilterItem
                        label={labelTrackingNumbers}
                        value={filters.trackingNumbers}
                        onChange={e => update({trackingNumbers: e.target.value})}
                        spanClassName={col1SpanClassName}
                        placeholder={t('podRiskReview.filter.trackingNumbersPlaceholder') as string}
                        rows={2}
                    />
                </div>
                <div className="min-w-0 col-span-3">
                    <BatchInputFilterItem
                        label={labelChannelOrderNumbers}
                        value={filters.channelOrderNumbers}
                        onChange={e => update({channelOrderNumbers: e.target.value})}
                        spanClassName={col1SpanClassName}
                        placeholder={
                            t('podRiskReview.filter.channelOrderNumbersPlaceholder') as string
                        }
                        rows={2}
                    />
                </div>

                {/* 第 3 行：位置维度，中心 → 仓库 → 区域 逐级收敛 */}
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelHub}
                        value={filters.hubIds}
                        onChange={handleHubChange}
                        options={hubOptions}
                        spanClassName={col1SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelWarehouse}
                        value={filters.warehouseIds}
                        onChange={handleWarehouseChange}
                        options={warehouseOptions}
                        spanClassName={col2SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelArea}
                        value={filters.areaSortingCodes}
                        onChange={value => update({areaSortingCodes: value})}
                        options={areaOptions}
                        spanClassName={col3SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>

                {/* 第 4 行：责任维度 */}
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelDsp}
                        value={filters.dspIds}
                        onChange={handleDspChange}
                        options={dspOptions}
                        spanClassName={col1SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    {/*
                        司机不复用 MultiSelectFilterItem：该组件只支持本地过滤，
                        未选配送商时候选集是全美司机、后端只返回前 50 条，须走远程搜索。
                    */}
                    <div className={containerClassName}>
                        <span className={labelCls2}>{labelDriver}</span>
                        <div className={inputContainerClassName}>
                            <Select
                                mode="multiple"
                                size="middle"
                                className="w-full"
                                maxTagCount="responsive"
                                maxTagTextLength={10}
                                options={driverOptions}
                                value={filters.driverIds}
                                onChange={val =>
                                    update({driverIds: val && val.length > 0 ? val : undefined})
                                }
                                loading={loading}
                                suffixIcon={<DownOutlined />}
                                placeholder={allPlaceholder}
                                showSearch
                                // 远程模式：候选集是截断结果，本地过滤会漏，交给后端 keyword 查；
                                // 本地模式：候选集已是完整范围，用 label 本地过滤，输入时不再发请求
                                filterOption={!isDriverRemoteMode}
                                optionFilterProp={isDriverRemoteMode ? undefined : 'label'}
                                onSearch={isDriverRemoteMode ? setDriverKeyword : undefined}
                                notFoundContent={
                                    isDriverRemoteMode &&
                                    driverKeyword.trim().length < DRIVER_SEARCH_MIN_LENGTH
                                        ? (t('podRiskReview.filter.driverSearchHint') as string)
                                        : undefined
                                }
                                allowClear
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <SingleSelectFilterItem
                        label={labelReviewer}
                        value={filters.reviewerId}
                        onChange={value => update({reviewerId: value})}
                        options={reviewerOptions}
                        spanClassName={col3SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>

                {/* 第 5 行：审核维度，审核时间同属该维度故并入本行 */}
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelStatus}
                        value={filters.statuses}
                        onChange={value =>
                            update({statuses: value as PodRiskReviewStatus[] | undefined})
                        }
                        options={statusOptions}
                        spanClassName={col1SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    <MultiSelectFilterItem
                        label={labelRiskTypes}
                        value={filters.riskTypeCodes}
                        onChange={value => update({riskTypeCodes: value})}
                        options={riskTypeOptions}
                        spanClassName={col2SpanClassName}
                        placeholder={allPlaceholder}
                    />
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls3}>{labelReviewedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.reviewedAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={range => {
                                    if (range && range[0] && range[1]) {
                                        update({reviewedAt: [range[0], range[1]]});
                                    } else {
                                        update({reviewedAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>

                {/* 第 6 行：妥投时间与创建时间，只选日期不带时分秒 */}
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls1}>{labelDeliveredAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                value={filters.deliveredAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={range => {
                                    if (range && range[0] && range[1]) {
                                        update({deliveredAt: [range[0], range[1]]});
                                    } else {
                                        update({deliveredAt: undefined});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <div className="min-w-0">
                    <div className={containerClassName}>
                        <span className={labelCls2}>{labelCreatedAt}</span>
                        <div className={inputContainerClassName}>
                            <RangePicker
                                size="middle"
                                className="w-full"
                                allowClear={false}
                                value={filters.createdAt ?? null}
                                disabledDate={buildOneMonthDisabledDate()}
                                onChange={range => {
                                    // 必填字段：忽略清空动作，仅在区间完整时才更新；
                                    // 当日 [00:00, 23:59:59.999] 的规整统一在转入参处做
                                    if (range && range[0] && range[1]) {
                                        update({createdAt: [range[0], range[1]]});
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PodRiskReviewFilter;
